import Layout from '../../Layout'
import React, { useEffect, useMemo, useState } from 'react';
import { Table, DatePicker, Spin, Input, Empty, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Search } from 'lucide-react';
import { SUPABASE_URL } from 'env';
import {
    getMarketingCampaignReport,
    MarketingCampaignReportData,
    MarketingReportCampaign,
} from '../../api/DashboardStatistics';

// ===================== HELPERS =====================

const fmt = (n?: number | null) => (n ?? 0).toLocaleString('en-US');

// Rút gọn tiền: 45000000 → "45,0" + "tr₫"; 1101678300 → "1,10" + "tỷ₫"
const compactVnd = (n?: number | null) => {
    const v = n ?? 0;
    if (Math.abs(v) >= 1e9) {
        return { val: (v / 1e9).toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), unit: 'tỷ₫' };
    }
    if (Math.abs(v) >= 1e6) {
        return { val: (v / 1e6).toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 }), unit: 'tr₫' };
    }
    return { val: fmt(v), unit: '₫' };
};

const CompactMoney = ({ value, unitClass = 'text-[13px]' }: { value?: number | null; unitClass?: string }) => {
    const c = compactVnd(value);
    return <>{c.val}<span className={unitClass}>{c.unit}</span></>;
};

const avatarUrl = (path?: string | null) => {
    if (!path) return null;
    return /^https?:\/\//.test(path) ? path : `${SUPABASE_URL}${path}`;
};

const BADGE_STYLES: Record<string, { icon: string; className: string }> = {
    'nhiều tin nhắn nhất': { icon: '🏆', className: 'bg-amber-500/15 text-amber-600' },
    'giá/tin thấp nhất': { icon: '💸', className: 'bg-amber-500/15 text-amber-600' },
    'tin nhắn giá trị nhất': { icon: '⭐', className: 'bg-teal-500/15 text-teal-600' },
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-baseline gap-2.5 my-11 mb-4">
        <h2 className=" text-[18px] font-semibold">{children}</h2>
        <span className="flex-1 h-[1px] dark:bg-white/[0.08]"></span>
    </div>
);

type Kpi = { label: string; val: React.ReactNode; unit?: React.ReactNode; border: string };

const KpiGrid = ({ items, className = '' }: { items: Kpi[]; className?: string }) => (
    <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 ${className}`}>
        {items.map((kpi, idx) => (
            <div key={idx} className={`bg-[#ffffff] dark:bg-[#1A2338] border dark:border-white/[0.08] rounded-xl p-4 relative overflow-hidden border-t-2 ${kpi.border}`}>
                <div className="text-[11.5px] text-[#8B93AC] mb-2.5 leading-snug min-h-[28px]">{kpi.label}</div>
                <div className="text-[21px] font-semibold text-000000e0">{kpi.val}</div>
                <div className="text-[11px] text-[#5C6483] mt-1">{kpi.unit}</div>
            </div>
        ))}
    </div>
);

// ===================== PAGE =====================

export default function MarketingReport() {
    const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs()]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<MarketingCampaignReportData | null>(null);
    const [keyword, setKeyword] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getMarketingCampaignReport({
            start_date: range[0].format('YYYY-MM-DD'),
            end_date: range[1].format('YYYY-MM-DD'),
        }).then((res) => {
            if (cancelled) return;
            if (res.status && res.data) {
                setData(res.data);
            } else {
                setError(res.msg || 'Không tải được dữ liệu báo cáo');
            }
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [range]);

    const period = data?.period;
    const s = data?.summary;
    const slaMinutes = period?.sla_minutes ?? 5;
    const mp = data?.channel_breakdown?.messages_proportion;
    const sp = data?.channel_breakdown?.spend_and_performance;
    const highlights = data?.campaign_highlights;
    const staffs = data?.staff_response_ranking ?? [];

    const filteredCampaigns = useMemo(() => {
        // BE trả nhiều dòng id = null và fb_campaign_id trùng → tự gán key theo vị trí
        const list = (data?.campaigns ?? []).map((c, i) => ({ ...c, _key: `${i}-${c.fb_campaign_id}` }));
        const kw = keyword.trim().toLowerCase();
        return kw ? list.filter((c) => c.name?.toLowerCase().includes(kw)) : list;
    }, [data, keyword]);

    // ----- KPI -----
    const roas = s && s.total_spent > 0 ? s.total_revenue / s.total_spent : null;

    const kpiRow1: Kpi[] = [
        { label: 'Tổng số chiến dịch marketing', val: fmt(s?.total_campaigns), unit: s?.total_campaigns_sub, border: 'border-t-[#2DD9C4]' },
        { label: 'Ngân sách được cấp', val: <CompactMoney value={s?.total_budget} />, unit: s?.total_budget_formatted, border: 'border-t-[#FFB84C]' },
        { label: 'Tổng tiền đã chạy (đã chi)', val: <CompactMoney value={s?.total_spent} />, unit: s?.spent_percent_formatted ?? s?.total_spent_formatted, border: 'border-t-[#FFB84C]' },
        { label: 'Tổng tin nhắn đổ về', val: fmt(s?.total_messages), unit: s?.total_messages_sub ?? 'tất cả nguồn', border: 'border-t-[#4C8DFF]' },
        { label: 'Tin nhắn từ quảng cáo', val: fmt(s?.ad_messages), unit: s?.ad_messages_sub, border: 'border-t-[#FF3B6B]' },
        { label: 'Tin nhắn tự nhiên (organic)', val: fmt(s?.organic_messages), unit: s?.organic_messages_sub, border: 'border-t-[#4ADE80]' },
    ];

    const kpiRow2: Kpi[] = [
        { label: 'Lượt bình luận trên bài/QC', val: fmt(s?.total_comments), unit: s?.total_comments_sub ?? 'chưa quy đổi thành tin nhắn', border: 'border-t-[#2DD9C4]' },
        {
            label: 'Chi phí trung bình / tin nhắn',
            val: <>{fmt(s?.avg_cost_per_message)}<span className="text-[12px]">₫</span></>,
            unit: s ? `${compactVnd(s.total_spent).val}${compactVnd(s.total_spent).unit} ÷ ${fmt(s.ad_messages)} tin QC` : null,
            border: 'border-t-[#FFB84C]',
        },
        {
            label: 'Tỷ lệ bình luận → tin nhắn',
            val: `${s?.comment_to_message_rate ?? 0}%`,
            unit: s ? `${fmt(s.total_comments)} bình luận → ${fmt(s.ad_messages)} tin QC` : null,
            border: 'border-t-[#4C8DFF]',
        },
        {
            label: 'Doanh thu quy đổi từ tin nhắn',
            val: <CompactMoney value={s?.total_revenue} />,
            unit: roas !== null ? `ROAS ≈ ${roas.toFixed(1)}x` : s?.total_revenue_formatted,
            border: 'border-t-[#4ADE80]',
        },
        {
            label: 'Thời gian phản hồi trung bình',
            val: <>{(s?.avg_response_minutes ?? 0).toFixed(1)}<span className="text-[12px]">phút</span></>,
            unit: (
                <Tooltip title={s ? `${s.overall_sla_rate}% tin nhắn được phản hồi trong ≤ ${slaMinutes} phút` : undefined}>
                    <span>{s?.staff_achieved_sla}</span>
                </Tooltip>
            ),
            border: 'border-t-[#2DD9C4]',
        },
        { label: 'Chiến dịch đang hoạt động', val: s?.active_campaigns_ratio ?? '0/0', unit: s?.active_campaigns_note, border: 'border-t-[#FF3B6B]' },
    ];

    // ----- Kênh -----
    const fbPercent = mp?.facebook?.percent ?? 0;
    const donutBg = mp && mp.total_messages > 0
        ? `conic-gradient(#4C8DFF 0deg ${fbPercent * 3.6}deg, #FF3B6B ${fbPercent * 3.6}deg 360deg)`
        : 'conic-gradient(#E5E7EB 0deg 360deg)';
    const totalChannelSpent = (sp?.facebook?.spent ?? 0) + (sp?.tiktok?.spent ?? 0);
    const spendShare = (v?: number) => (totalChannelSpent > 0 ? ((v ?? 0) / totalChannelSpent) * 100 : 0);

    const channelSpendRows = [
        { item: sp?.facebook, color: '#4C8DFF' },
        { item: sp?.tiktok, color: '#FF3B6B' },
    ];

    // ----- Bảng chiến dịch -----
    const campaignColumns = [
        {
            title: 'Chiến dịch',
            dataIndex: 'name',
            key: 'name',
            width: 340,
            render: (text: string, record: MarketingReportCampaign) => (
                <div>
                    <div className="font-semibold text-gray-900 text-000000e0 text-[13.5px] flex items-center gap-2 flex-wrap">
                        <span>{text}</span>
                        {record.effective_status !== 'ACTIVE' && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-500/10 text-gray-500 uppercase">Tạm dừng</span>
                        )}
                    </div>
                    <div className="text-[11px] text-[#8B93AC] mt-0.5">
                        {fmt(record.ad_sets_count)} nhóm QC · {fmt(record.ads_count)} mẫu QC · {fmt(record.leads)} khách tiềm năng
                    </div>
                    {record.badges?.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap mt-1">
                            {record.badges.map((b) => {
                                const st = BADGE_STYLES[b] ?? { icon: '✨', className: 'bg-amber-500/15 text-amber-600' };
                                return (
                                    <span key={b} className={`inline-flex items-center gap-1 text-[10.5px] px-2 py-0.5 rounded-md ${st.className}`}>
                                        {st.icon} {b}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Kênh',
            dataIndex: 'channel',
            key: 'channel',
            render: (channel: string) => (
                <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${channel === 'Facebook'
                    ? 'bg-blue-500/10 text-blue-600 dark:text-[#4C8DFF]'
                    : 'bg-pink-500/10 text-pink-600 dark:text-[#FF3B6B]'
                    }`}>
                    ● {channel}
                </span>
            ),
        },
        {
            title: 'Ngân sách',
            dataIndex: 'budget_formatted',
            key: 'budget',
            className: 'text-[#8B93AC] whitespace-nowrap',
            sorter: (a: MarketingReportCampaign, b: MarketingReportCampaign) => a.budget - b.budget,
        },
        {
            title: 'Đã chi',
            dataIndex: 'spent_formatted',
            key: 'spent',
            className: 'text-[#8B93AC] whitespace-nowrap',
            sorter: (a: MarketingReportCampaign, b: MarketingReportCampaign) => a.spent - b.spent,
        },
        {
            title: 'Tin nhắn QC',
            dataIndex: 'messages',
            key: 'messages',
            sorter: (a: MarketingReportCampaign, b: MarketingReportCampaign) => a.messages - b.messages,
            render: (val: number, record: MarketingReportCampaign) => (
                <span className={`${record.is_most_messages ? 'text-emerald-500 font-bold' : 'text-000000e0'}`}>
                    {fmt(val)}
                </span>
            ),
        },
        {
            title: 'Giá / tin',
            dataIndex: 'cost_per_message_formatted',
            key: 'cost_per_message',
            className: 'whitespace-nowrap',
            sorter: (a: MarketingReportCampaign, b: MarketingReportCampaign) => a.cost_per_message - b.cost_per_message,
            render: (val: string, record: MarketingReportCampaign) => (
                <span className={`${record.is_lowest_cost ? 'text-emerald-500 font-bold' : 'text-000000e0'}`}>
                    {record.messages > 0 && record.spent > 0 ? val : '—'}
                </span>
            ),
        },
        {
            title: 'Doanh thu quy đổi',
            dataIndex: 'revenue_formatted',
            key: 'revenue',
            className: 'whitespace-nowrap',
            sorter: (a: MarketingReportCampaign, b: MarketingReportCampaign) => a.revenue - b.revenue,
            render: (val: string, record: MarketingReportCampaign) => (
                <span className={`${record.is_highest_revenue ? 'text-teal-500 font-bold' : 'text-000000e0'}`}>
                    {val}
                </span>
            ),
        },
    ];

    const highlightCards = [
        { title: '🏆 Chiến dịch nhiều tin nhắn nhất', val: highlights?.most_messages?.display, color: 'text-emerald-500' },
        { title: '💸 Chiến dịch giá/tin thấp nhất', val: highlights?.lowest_cost?.display, color: 'text-emerald-500' },
        { title: '⭐ Chiến dịch tin nhắn giá trị nhất', val: highlights?.highest_revenue?.display, color: 'text-teal-500' },
        { title: '📊 Trung bình chi phí / tin nhắn', val: highlights?.avg_cost_per_message?.display ?? s?.avg_cost_per_message_formatted, color: 'text-000000e0' },
    ];

    // Thanh đường đua: vạch SLA nằm giữa (50%), vượt 2×SLA thì chấm dừng ở mép phải
    const trackPercent = (minutes: number) => Math.max(0, Math.min(100, (minutes / (slaMinutes * 2)) * 100));

    return (
        <Layout>
            <div className="max-w-full mx-auto text-000000e0">

                {/* ===================== HEADER ===================== */}
                <header className="flex justify-between items-end gap-5 pb-6 mb-7 border-b flex-wrap dark:border-white/[0.08]">
                    <div>
                        <div className="text-[11.5px] tracking-[0.14em] uppercase text-[#2DD9C4] mb-2.5 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#2DD9C4] shadow-[0_0_8px_#2DD9C4]"></span>
                            Báo cáo hiệu suất Marketing
                        </div>
                        <h1 className=" text-2xl sm:text-[32px] font-bold tracking-tight text-000000e0">
                            Tổng quan chiến dịch &amp; tin nhắn
                        </h1>
                        <div className="text-[#8B93AC] text-sm mt-1.5">
                            Facebook Ads &amp; TikTok Ads · Dữ liệu tổng hợp toàn bộ chiến dịch đang chạy
                        </div>
                    </div>
                    <div className="flex flex-col items-start sm:items-end gap-2">
                        <DatePicker.RangePicker
                            value={range}
                            onChange={(dates) => { if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]); }}
                            allowClear={false}
                            format="DD/MM/YYYY"
                            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
                        />
                        <div className="text-[12.5px] dark:bg-[#1A2338] border px-3.5 py-2 rounded-lg text-000000e0 whitespace-nowrap">
                            Kỳ báo cáo: <b className="text-000000e0 font-semibold">{period?.display ?? `${range[0].format('DD/MM/YYYY')} – ${range[1].format('DD/MM/YYYY')}`}</b>
                            {period?.working_hours && <span className="text-[#8B93AC]"> · Ca trực {period.working_hours}</span>}
                        </div>
                    </div>
                </header>

                {error && (
                    <div className="mb-4 px-4 py-3 rounded-lg border border-rose-300 bg-rose-50 text-rose-600 text-sm dark:bg-rose-500/10 dark:border-rose-500/30">
                        {error}
                    </div>
                )}

                <Spin spinning={loading}>
                    {/* ===================== 1. CHỈ SỐ TỔNG QUAN ===================== */}
                    <SectionTitle>Chỉ số tổng quan bộ phận Marketing phải nắm</SectionTitle>
                    <KpiGrid items={kpiRow1} />
                    <KpiGrid items={kpiRow2} className="mt-3" />

                    {/* ===================== 2. KÊNH ===================== */}
                    <SectionTitle>Nguồn kênh — Facebook vs TikTok</SectionTitle>

                    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.6fr] gap-4">
                        {/* Card Donut */}
                        <div className="bg-[#ffffff] dark:bg-[#1A2338] border dark:border-white/[0.08] rounded-2xl p-[22px]">
                            <h3 className=" text-[14.5px] font-semibold mb-[18px]">Tỷ trọng tin nhắn theo kênh</h3>
                            <div className="flex items-center gap-[22px] flex-wrap">
                                <div className="w-[150px] h-[150px] rounded-full shrink-0 flex items-center justify-center relative" style={{ background: donutBg }}>
                                    <div className="absolute w-[96px] h-[96px] rounded-full bg-[#ffffff] dark:bg-[#1A2338]"></div>
                                    <div className="relative z-10 text-center">
                                        <span className="text-[20px] font-bold">{fmt(mp?.total_messages)}</span><br />
                                        <span className="text-[9.5px] text-[#5C6483] uppercase tracking-wider">tin nhắn</span>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-3 flex-1 min-w-[180px]">
                                    {[
                                        { item: mp?.facebook, color: 'bg-[#4C8DFF]', fallback: 'Facebook' },
                                        { item: mp?.tiktok, color: 'bg-[#FF3B6B]', fallback: 'TikTok' },
                                    ].map(({ item, color, fallback }) => (
                                        <div key={fallback} className="flex justify-between items-center text-[13px]">
                                            <span className="flex items-center text-[#8B93AC]"><span className={`w-[9px] h-[9px] rounded-full ${color} inline-block mr-2`}></span>{item?.name ?? fallback}</span>
                                            <span className="text-000000e0 font-semibold">{fmt(item?.count)} <span className="text-[#5C6483] text-[11px] inline">({item?.percent ?? 0}%)</span></span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Card Split Bars */}
                        <div className="bg-[#ffffff] dark:bg-[#1A2338] border dark:border-white/[0.08] rounded-2xl p-[22px]">
                            <h3 className=" text-[14.5px] font-semibold mb-[18px]">Chi tiêu &amp; hiệu suất theo kênh</h3>

                            {channelSpendRows.map(({ item, color }, idx) => (
                                <div className="mb-4" key={idx}>
                                    <div className="flex justify-between text-[12.5px] text-[#8B93AC] mb-[7px] gap-3">
                                        <span>{item?.campaign_label}</span>
                                        <b className={item?.is_integrated === false ? 'text-[#8B93AC] font-medium italic' : 'text-000000e0'}>{item?.spent_formatted}</b>
                                    </div>
                                    <div className="h-[9px] bg-gray-100 dark:bg-white/[0.06] rounded-[5px] overflow-hidden flex">
                                        <span className="h-full" style={{ width: `${spendShare(item?.spent)}%`, background: color }}></span>
                                    </div>
                                </div>
                            ))}

                            {channelSpendRows.map(({ item }, idx) => (
                                <div className={idx === 0 ? 'mb-4' : ''} key={`cpm-${idx}`}>
                                    <div className="flex justify-between text-[12.5px] text-[#8B93AC]">
                                        <span className="text-[#5C6483]">Giá / tin nhắn trung bình — {item?.name ?? (idx === 0 ? 'Facebook' : 'TikTok')}</span>
                                        <b className="text-000000e0 ">{item?.avg_cost_per_message_formatted ?? 'N/A'}</b>
                                    </div>
                                </div>
                            ))}

                            {sp?.tiktok?.is_integrated === false && sp.tiktok.note && (
                                <div className="mt-4 text-[11.5px] text-amber-600 bg-amber-500/10 rounded-md px-3 py-2">
                                    ⚠️ {sp.tiktok.note}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ===================== 3. CHIẾN DỊCH ===================== */}
                    <SectionTitle>Hiệu suất từng chiến dịch</SectionTitle>

                    <div className="bg-[#ffffff] dark:bg-[#1A2338] border dark:border-white/[0.08] rounded-2xl p-2 overflow-x-auto">
                        <div className="flex justify-between items-center gap-3 px-2 pt-2 pb-3 flex-wrap">
                            <span className="text-[12.5px] text-[#8B93AC]">
                                {fmt(filteredCampaigns.length)} chiến dịch{keyword ? ` khớp "${keyword}"` : ''}
                            </span>
                            <Input
                                allowClear
                                prefix={<Search size={14} className="text-[#8B93AC]" />}
                                placeholder="Tìm tên chiến dịch"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                className="max-w-[280px]"
                            />
                        </div>
                        <Table
                            rowKey="_key"
                            dataSource={filteredCampaigns}
                            columns={campaignColumns}
                            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100] }}
                            className="custom-ant-table min-w-[1000px]"
                            locale={{ emptyText: <Empty description="Không có chiến dịch trong kỳ báo cáo" /> }}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mt-4">
                        {highlightCards.map((item, idx) => (
                            <div key={idx} className="dark:bg-[#1A2338] border dark:border-white/[0.08] rounded-xl p-[18px] bg-[#ffffff] min-w-0">
                                <div className="text-[11.5px] text-[#8B93AC] mb-2">{item.title}</div>
                                <div className={`text-[17px] font-bold leading-snug break-words ${item.color}`}>{item.val || '—'}</div>
                            </div>
                        ))}
                    </div>

                    {/* ===================== 4. THỜI GIAN PHẢN HỒI ===================== */}
                    <SectionTitle>Thời gian phản hồi tin nhắn theo nhân viên</SectionTitle>

                    <div className="bg-[#ffffff] dark:bg-[#1A2338] border dark:border-white/[0.08] rounded-2xl p-[26px]">
                        <div className="flex justify-between items-start mb-[26px] flex-wrap gap-2.5">
                            <h3 className=" text-[14.5px] font-semibold">Đường đua phản hồi — mốc trung tâm là ngưỡng SLA</h3>
                            <div className="text-xs text-[#8B93AC]">
                                Yêu cầu: mọi tin nhắn phải được phản hồi trong tối đa <b className="text-[#FFB84C] ">{slaMinutes} phút</b>
                                {period?.working_hours && <> · chỉ tính trong ca {period.working_hours}</>}
                            </div>
                        </div>

                        {staffs.length === 0 ? (
                            <Empty description="Chưa có dữ liệu phản hồi tin nhắn trong kỳ" />
                        ) : (
                            <div className="flex flex-col gap-[22px]">
                                {staffs.map((staff) => {
                                    const pass = staff.status === 'pass';
                                    const percent = trackPercent(staff.avg_response_minutes);
                                    const color = pass ? '#4ADE80' : '#FF5C72';
                                    const img = avatarUrl(staff.avatar);
                                    return (
                                        <div key={staff.user_id} className="grid grid-cols-1 md:grid-cols-[220px_1fr_90px] items-center gap-[18px]">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                {img ? (
                                                    <img src={img} alt={staff.name} className="w-8 h-8 rounded-full object-cover shrink-0" />
                                                ) : (
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${pass ? 'bg-emerald-400 text-gray-950' : 'bg-rose-500 text-white'}`}>
                                                        {staff.initials}
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <div className="text-[13px] font-semibold text-000000e0 truncate" title={staff.name}>{staff.name}</div>
                                                    <div className="text-[10.5px] text-[#5C6483]">
                                                        {fmt(staff.processed_messages)} tin nhắn xử lý · {staff.sla_rate_percent}% đạt SLA
                                                    </div>
                                                </div>
                                            </div>

                                            <Tooltip title={`Trung bình ${staff.avg_response_minutes} phút / tin nhắn`}>
                                                <div className="relative h-[26px] bg-[#ccc] dark:bg-white/[0.08] rounded-md">
                                                    <div className="absolute top-[-4px] bottom-[-4px] left-1/2 w-[1px] bg-[#FFB84C] opacity-50">
                                                        <span className="sm:top-[-12px] top-auto bottom-[-26px] sm:bottom-auto text-[9px] text-[#5C6483] dark:text-[#FFB84C] whitespace-nowrap customer-absolute-center">{slaMinutes} phút</span>
                                                    </div>
                                                    <div className="absolute top-0 bottom-0 left-0 rounded-md opacity-35" style={{ width: `${percent}%`, background: color }}></div>
                                                    <div className="absolute top-[6px] w-[14px] h-[14px] rounded-full -translate-x-1/2 border-[2.5px] border-[#121A2B] shadow-[0_0_0_1px_rgba(255,255,255,0.15)]" style={{ left: `${percent}%`, background: color }}></div>
                                                </div>
                                            </Tooltip>

                                            <div className={`text-[13px] font-bold text-right ${pass ? 'text-emerald-500' : 'text-rose-500'}`}>
                                                {staff.time_display}
                                                <span className="block text-[9.5px] font-medium text-[#5C6483] uppercase mt-0.5">
                                                    {staff.status_label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Spin>

            </div>
        </Layout>
    );
}
